import { current } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ItemDTO, ItemCustomFieldValue, OrderDTO, OrderTotalsDTO, PrintedTicketLinkDTO, SellerSimpleSummaryDTO, SellerSummaryDTO,
} from '../../models/order';
import { Response } from '../../models/shared';
import TicketDTO from '../../models/ticket';
import baseQueryWithReauth from './base';
import EventDTO, { EventSalesReportDTO, EventAudienceDiscountDTO, EventSalesByUserDTO } from '../../models/event';
import { PollData } from '../../models/poll';
// eslint-disable-next-line import/no-cycle
import { ticketsApi } from './tickets';
import { UserEvent } from '../../models/user';
import { PaymentMethod, PaymentProvider } from '../../models/payment';
// eslint-disable-next-line import/no-cycle
import { privateEventsApi } from './private-events';
// eslint-disable-next-line import/no-cycle
import { businessesApi } from './business';
import OrderItem from '../../types/OrderItem';
import { AbandonedCartAttribution, AbandonedCartStep } from '../../utils/abandonedCartTracking';
import { StaffMemberStatus, StaffRole } from '../../models/staff';
import { linkedItemFor, unlinkedItemFor } from '../../utils/printedTicketLink';
import { CheckoutContextDTO } from '../../models/checkout-event';

export interface ItemData {
  name: string;
  last_name: string;
  person_id: string;
  ticket_id: string;
  ticket?: TicketDTO,
  custom_fields?: ItemCustomFieldValue[];
}
export interface PayWayTokenData {
  token: string; // Token ID from Decidir SDK after tokenizing card
  bin: string; // First 6 digits of card number
  payment_method_id: number; // 1=Visa, 15=Mastercard, 65=Amex
  installments?: number; // Optional, defaults to 1
  device_unique_identifier?: string; // Cybersource fingerprint session id, max 128 chars
  card_holder_name?: string; // Max 100 chars
  card_holder_doc_number?: string; // Max 20 chars
  card_last_digits?: string; // From the Decidir token response, stored with the payment
  billing_address?: {
    street1: string;
    city: string;
    state: string; // ISO 3166-2:AR letter code, e.g. 'C'
    postal_code: string;
  };
}

export interface NavePaymentData {
  payment_request_id: string; // UUID v4 from backend payment request
  status: 'approved' | 'rejected' | 'pending' | 'cancelled' | 'error';
  payment_id?: string; // Payment ID from Nave SDK on success
}

export interface OrderData {
  email: string;
  phone?: string;
  instagram_user?: string;
  items: ItemData[];
  open_price_amount?: number;
  coupon_id?: string;
  poll_responses?: PollData[];
  is_manual?: boolean;
  skip_poll?: boolean;
  referral_code?: string | null;
  // Atribución de la campaña de carrito abandonado: el token que viajó en el
  // CTA del mail, guardado en localStorage hasta que arranca el evento.
  abandoned_cart_token?: string | null;
  abandoned_cart_step?: AbandonedCartStep | null;
  abandoned_cart_attribution?: AbandonedCartAttribution | null;
  from_backoffice: boolean;
  is_seller_order: boolean;
  is_cashier_order?: boolean;
  // In-person sales: the open cash register session making the sale.
  cash_register_session_id?: string;
  // In-person sales: the productora's payment method the cashier charges with.
  cash_payment_method_id?: string;
  payment_method?: PaymentMethod;
  payment_provider?: PaymentProvider;
  reservation_code?: string;
  payway_token?: PayWayTokenData; // Required when payment_provider === "PAYWAY", forbidden otherwise
  nave_payment?: NavePaymentData; // Required when payment_provider === "NAVE", forbidden otherwise
  fiserv_installments?: number; // Only with payment_provider === "FISERV"
  // Promoción bancaria de Fiserv elegida: el backend cobra con su tienda, que
  // solo acepta los BIN del banco. Only with payment_provider === "FISERV".
  fiserv_promotion_id?: string;
  // Box office: one key per sale attempt so the backend can drop a duplicate
  // of the same sale (double tap on "Confirmar venta"). Optional and ignored
  // by a backend that does not support it.
  idempotency_key?: string;
  // Medición: versión del checkout, UTM, fbclid, gclid y términos aceptados.
  // Solo en compras del público.
  checkout_context?: CheckoutContextDTO;
}

export interface DecodedItemData {
  item_id: string;
  order_id: string;
  name: string;
  last_name: string;
  person_id: string;
  order_status: string;
  valid: boolean;
  marked_used_by: number;
  used_at: string | null;
  // Who took the ticket and through which door, for "ya entró". Null when the
  // scanner had no door, or the ticket was used before the door was recorded.
  marked_used_by_name?: string | null;
  used_at_door_id?: string | null;
  used_at_door_name?: string | null;
  event_id: string,
  ticket_id: string,
  event?: EventDTO
  ticket?: TicketDTO
  accepted_on_decode: boolean;
  is_expired: boolean;
  is_not_yet_scannable: boolean;
  seat_name?: string;
  audience_discount?: EventAudienceDiscountDTO | null;
  showtime_id: string | null;
  last_door_scanned_at_id?: string | null;
  attendance_arrival?: string | null;
  attendance_exit?: string | null;
  attendance_recorded_on_decode?: 'arrival' | 'exit' | null;
  // The rest of the order, sent with every scan so "Ver orden completa" opens
  // without another request. Missing on backends older than this field.
  order_display_id?: string;
  order_items?: DecodedOrderQrData['items'];
}

export interface DecodedOrderQrData {
  order_id: string;
  custom_id: string;
  display_id: string;
  items: Array<{
    item_id: string;
    name: string;
    last_name: string;
    person_id: string;
    marked_used_by: number | null;
    used_at: string | null;
    ticket_id: string;
    ticket: Pick<TicketDTO, 'id' | 'name' | 'scanning_ends_at' | 'showtime_id'> | null;
    audience_discount?: EventAudienceDiscountDTO | null;
    last_door_scanned_at_id?: string | null;
    seat_name?: string | null;
    // False when this ticket does not go in through the scanner's door. Only
    // the order QR sends it; missing means allowed.
    allowed_at_door?: boolean;
  }>;
  valid: boolean;
}

export interface PartialOrderData {
  event_id: string;
  items: {
    person_id: string;
    ticket_id: string;
  }[];
}

export interface OrderReferralData {
  order: OrderDTO;
  orders_by_referral: { buyer_name: string, amount: number, created_at: string, items_count: number }[];
  progress: {
    progress_percentage: number;
    reimburse_amount: number;
    limit_amount: number;
  };
}

export interface DecodedSellerQRData {
  user_event: UserEvent;
}

export interface DecodedStaffQRData {
  type: 'staff';
  id: string;
  event_id: string;
  role_id: string;
  first_name: string;
  last_name: string;
  dni: string;
  email: string;
  phone: string | null;
  birth_date: string | null;
  photo_url: string | null;
  is_role_responsible: boolean;
  notes: string | null;
  status: StaffMemberStatus;
  qr_sent_at: string | null;
  scan_count: number;
  last_scanned_at: string | null;
  created_at: string;
  updated_at: string;
  role?: Pick<StaffRole, 'id' | 'name' | 'color'> | null;
}

export type DecodedQrData = DecodedItemData | DecodedSellerQRData | DecodedOrderQrData | DecodedStaffQRData;

const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['orders', 'qrcode', 'seller_summary'],
  endpoints: (build) => ({
    createOrder: build.mutation<Response<OrderDTO>, { order: OrderData, businessId: string, eventId: string }>({
      query: ({
        order, businessId, eventId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        method: 'POST',
        body: {
          ...order,
        },
      }),
      invalidatesTags: ['orders', 'seller_summary'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ticketsApi.util.invalidateTags(['tickets']));
          if (arg.order.cash_register_session_id) {
            dispatch(businessesApi.util.invalidateTags(['cash-register-sessions']));
          }
          if (arg.order.is_manual) {
            dispatch(privateEventsApi.util.invalidateTags(['private-events-balance']));
          }
        } catch (e) {
          console.error(e);
        }
      },
    }),
    getEventOrders: build.query<Response<OrderDTO[]>, { businessId: string, eventId: string }>({
      providesTags: ['orders'],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        method: 'GET',
      }),
    }),
    getEventOrderItems: build.query<OrderItem[] | null, { businessId: string, eventId: string, userId?: string }>({
      providesTags: ['orders'],
      query: ({ businessId, eventId, userId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        method: 'GET',
        params: {
          flat: 'true',
          created_by_id: userId,
        },
      }),
      transformResponse: (response: Response<OrderItem[]>): (OrderItem[] | null) => response.data || null,
    }),
    getSellerOrders: build.query<Response<OrderDTO[]>, { businessId: string, eventId: string }>({
      providesTags: ['orders'],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/data`,
        method: 'GET',
      }),
    }),
    getOrderById: build.query<OrderDTO | null, { orderId: string }>({
      // Per order, so linking a printed ticket can reload only this one.
      // Invalidating the bare 'orders' tag still reaches it.
      providesTags: (_result, _error, { orderId }) => [{ type: 'orders', id: orderId }],
      query: ({ orderId }) => ({
        url: `/orders/${orderId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<OrderDTO>): (OrderDTO | null) => response.data || null,
    }),
    getOrderByCustomId: build.query<OrderDTO | null, { customId: string }>({
      providesTags: ['orders'],
      query: ({ customId }) => ({
        url: `/orders/custom-id/${customId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<OrderDTO>): (OrderDTO | null) => response.data || null,
    }),
    // The purchase email is the proof of ownership: the number alone is printed on every
    // ticket. The response is a reduced order (no ids, masked document).
    getPublicOrderByDisplayId: build.query<OrderDTO | null, { eventId: string, displayId: string, email: string }>({
      providesTags: ['orders'],
      query: ({ eventId, displayId, email }) => ({
        url: `/events/${eventId}/orders/display-id/${displayId}`,
        params: { email },
        method: 'GET',
      }),
      transformResponse: (response: Response<OrderDTO>): (OrderDTO | null) => response.data || null,
    }),
    updateOrderPublicEmail: build.mutation<Response<OrderDTO>, { customId: string, email: string }>({
      query: ({ customId, email }) => ({
        url: `/orders/custom-id/${customId}/public-email`,
        method: 'PATCH',
        body: { email },
      }),
      invalidatesTags: ['orders'],
    }),
    requestPublicRefund: build.mutation<Response<OrderDTO>, { eventId: string, displayId: string, email: string }>({
      query: ({ eventId, displayId, email }) => ({
        url: `/events/${eventId}/orders/display-id/${displayId}/public-refund`,
        method: 'POST',
        body: { email },
      }),
      invalidatesTags: ['orders'],
    }),
    getEventOrdersExport: build.query<void, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        params: {
          xlsx: true,
        },
        method: 'GET',
      }),
    }),
    decodeQrCode: build.query<(DecodedQrData | null), { data: string | null, personId?: string, eventId: string, businessId: string, hasOrderQr: boolean }>({
      providesTags: ['qrcode'],
      query: ({
        data, eventId, businessId, personId, hasOrderQr,
      }) => ({
        url: hasOrderQr ? `/businesses/${businessId}/events/${eventId}/decode-order` : `/businesses/${businessId}/events/${eventId}/decode`,
        params: {
          data: data || undefined,
          person_id: personId,
        },
        method: 'GET',
      }),
      transformResponse: (response: Response<DecodedQrData>): (DecodedQrData | null) => response.data || null,
      // Sin toast: el escáner muestra el error en su propia pantalla de resultado
      // y un toast encima tapaba el nombre y la puerta.
      extraOptions: { silent: true },
      // Cada escaneo es un pedido nuevo. Con caché, volver a leer el mismo QR devolvía la
      // respuesta vieja sin ir al backend (con auto-aceptar, un "aceptado" que ya no era).
      keepUnusedDataFor: 0,
    }),
    updateOrder: build.mutation<Response<OrderDTO>, { order: Partial<OrderDTO>, businessId: string, eventId: string, orderId: string }>({
      query: ({
        order, businessId, eventId, orderId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders`,
        method: 'PUT',
        body: { ...order, id: orderId },
      }),
      invalidatesTags: ['orders'],
    }),
    updateOrderStatus: build.mutation<Response<OrderDTO>, { newStatus: string, businessId: string, eventId: string, orderId: string }>({
      query: ({
        newStatus, businessId, eventId, orderId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/status`,
        method: 'PATCH',
        body: { status: newStatus },
      }),
      invalidatesTags: ['orders'],
    }),
    updateOrderItem: build.mutation<Response<ItemDTO>, { item: Partial<ItemDTO>, businessId: string, eventId: string, orderId: string, itemId: string }>({
      query: ({
        item, businessId, eventId, orderId, itemId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/items`,
        method: 'PUT',
        body: { ...item, id: itemId },
      }),
      invalidatesTags: ['orders'],
    }),
    deleteOrderItem: build.mutation<Response<ItemDTO>, { businessId: string, eventId: string, orderId: string, itemId: string }>({
      query: ({
        businessId, eventId, orderId, itemId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/items/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['orders', 'seller_summary'],
    }),
    deleteOrder: build.mutation<Response<OrderDTO>, { businessId: string, eventId: string, orderId: string }>({
      query: ({
        businessId, eventId, orderId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['orders', 'seller_summary'],
    }),
    updateUsedOrderItem: build.mutation<Response<ItemDTO>, { businessId: string, eventId: string, orderId: string, itemId: string }>({
      query: ({
        businessId, eventId, orderId, itemId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/items/${itemId}/used`,
        method: 'PATCH',
      }),
      // Only a confirmed scan changes the decoded data. Refetching after a
      // rejection would repaint the scanner over the error the API returned.
      invalidatesTags: (_result, confirmError) => (confirmError ? [] : ['qrcode']),
    }),
    changeOrderEmail: build.mutation<Response<OrderDTO>, { businessId: string, eventId: string, orderId: string, email: string }>({
      query: ({
        businessId, eventId, orderId, email,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/email`,
        method: 'PATCH',
        body: { email },
      }),
      invalidatesTags: ['orders'],
    }),
    regenerateOrderQrs: build.mutation<Response<OrderDTO>, { businessId: string, eventId: string, orderId: string }>({
      query: ({
        businessId, eventId, orderId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/actions/regenerate-qr`,
        method: 'POST',
      }),
      invalidatesTags: ['orders'],
    }),
    sendOrderEmail: build.mutation<Response<void>, { businessId: string, eventId: string, customId: string }>({
      // Reenviar abre un seguimiento nuevo del mail: la columna Mail de
      // Asistentes tiene que mostrar el estado del envío nuevo.
      invalidatesTags: ['orders'],
      query: ({
        businessId, eventId, customId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${customId}/email`,
        method: 'POST',
      }),
    }),
    getSellerSummary: build.query<SellerSummaryDTO[] | null, { businessId: string, eventId: string }>({
      providesTags: ['orders', 'seller_summary'],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/summary`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerSummaryDTO[]>): (SellerSummaryDTO[] | null) => response.data || null,
    }),
    getQuickSellerSummary: build.query<SellerSimpleSummaryDTO[] | null, { businessId: string, eventId: string }>({
      providesTags: ['orders', 'seller_summary'],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/summary-quick`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerSimpleSummaryDTO[]>): (SellerSimpleSummaryDTO[] | null) => response.data || null,
    }),
    getOrderTotals: build.query<OrderTotalsDTO | null, { businessId: string, eventId: string, order: OrderData }>({
      providesTags: ['orders'],
      query: ({ businessId, eventId, order }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/amounts`,
        method: 'POST',
        body: order,
      }),
      transformResponse: (response: Response<OrderTotalsDTO>): (OrderTotalsDTO | null) => response.data || null,
    }),
    getOneSellerSummary: build.query<SellerSummaryDTO | null, { businessId: string, eventId: string, sellerId: string }>({
      providesTags: ['orders', 'seller_summary'],
      query: ({ businessId, eventId, sellerId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/summary/${sellerId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<SellerSummaryDTO>): (SellerSummaryDTO | null) => response.data || null,
    }),
    getEventOrdersReport: build.query<EventSalesReportDTO | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'orders' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/report/sales`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventSalesReportDTO>): (EventSalesReportDTO | null) => response.data || null,
    }),
    getQRImage: build.query<{ qr_base64: string[] }, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `/orders/${orderId}/qr-image`,
        method: 'GET',
      }),
      transformResponse: (response: Response<{ qr_base64: string[] }>): { qr_base64: string[] } => response.data || { qr_base64: [] },
    }),
    getOrderReferralData: build.query<OrderReferralData | null, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `/orders/${orderId}/referral`,
        method: 'GET',
      }),
      transformResponse: (response: Response<OrderReferralData>): (OrderReferralData | null) => response.data || null,
    }),
    validateTicketExclusive: build.mutation<boolean, { businessId: string, eventId: string, order: PartialOrderData }>({
      query: ({ businessId, eventId, order }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/actions/ticket-exclusive`,
        method: 'POST',
        body: order,
      }),
      transformResponse: (response: Response<boolean>): boolean => response.data || false,
    }),
    getSellerSummaryXlsx: build.mutation<void, { businessId: string, eventId: string }>({
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/sellers/summary/xlsx`,
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `sellers_summary_${new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })}-${businessId}.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
    getEventSalesByUser: build.query<EventSalesByUserDTO[] | null, { businessId: string, eventId: string }>({
      providesTags: () => [{ type: 'orders' }],
      query: ({ businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/report/sales-by-user`,
        method: 'GET',
      }),
      transformResponse: (response: Response<EventSalesByUserDTO[]>): (EventSalesByUserDTO[] | null) => response.data || null,
    }),
    // Linking and unlinking patch the order in the cache with the item the API
    // answers with, instead of reloading every order: a box-office sale links
    // its tickets one scan after another, and reloading the order with all its
    // relations after each one held the next scan back. When the answer cannot
    // be merged (an older API, an order not in the cache), only that order is
    // reloaded. The lists and reports that also hold orders are not on screen
    // while linking; PrintedTicketsLinker refreshes them when it closes.
    linkPrintedTicket: build.mutation<Response<PrintedTicketLinkDTO>, { businessId: string, eventId: string, orderId: string, itemId: string, code: string }>({
      query: ({
        businessId, eventId, orderId, itemId, code,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/items/${itemId}/printed-ticket`,
        method: 'POST',
        body: { code },
      }),
      // The linker shows the rejection inline next to the scan.
      extraOptions: { silent: true },
      async onQueryStarted({ orderId, itemId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(businessesApi.util.invalidateTags(['cash-register-sessions']));
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          patchOrderItem(dispatch, orderId, itemId, (previous) => linkedItemFor(previous, data.data));
        } catch {
          // The cashier sees the rejection where they scanned.
        }
      },
    }),
    unlinkPrintedTicket: build.mutation<Response<PrintedTicketLinkDTO>, { businessId: string, eventId: string, orderId: string, itemId: string }>({
      query: ({
        businessId, eventId, orderId, itemId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/${orderId}/items/${itemId}/printed-ticket`,
        method: 'DELETE',
      }),
      async onQueryStarted({ orderId, itemId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(businessesApi.util.invalidateTags(['cash-register-sessions']));
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          patchOrderItem(dispatch, orderId, itemId, (previous) => unlinkedItemFor(previous, data.data));
        } catch {
          // The error toast comes from baseQueryWithReauth.
        }
      },
    }),
    // unlinked (printed ticket types only): the tickets sit unpaid and every
    // door refuses them until a box-office sale links one.
    createBatchManualOrders: build.mutation<Response<void>, {
      businessId: string, eventId: string, ticketId: string, name: string, amount: number, unlinked?: boolean,
    }>({
      query: ({
        businessId, eventId, ticketId, name, amount, unlinked,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/orders/actions/batch-manual`,
        method: 'POST',
        body: {
          ticket_id: ticketId,
          name,
          amount,
          ...(unlinked ? { unlinked: true } : {}),
        },
      }),
      invalidatesTags: ['orders'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(privateEventsApi.util.invalidateTags(['private-events-balance']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
  }),
});

type OrdersCacheAction = ReturnType<typeof ordersApi.util.updateQueryData> | ReturnType<typeof ordersApi.util.invalidateTags>;

// Swaps one item of a cached order for the one `nextItemOf` builds from it. If
// it builds none, or the order or the item is not in the cache, the order is
// reloaded instead: a stale order would send the next scan to a ticket that no
// longer exists.
function patchOrderItem(
  dispatch: (action: OrdersCacheAction) => unknown,
  orderId: string,
  itemId: string,
  nextItemOf: (previous: ItemDTO) => ItemDTO | null,
) {
  let patched = false;
  dispatch(ordersApi.util.updateQueryData('getOrderById', { orderId }, (draft) => {
    const index = draft ? draft.items.findIndex((item) => item.id === itemId) : -1;
    if (!draft || index < 0) {
      return;
    }
    const next = nextItemOf(current(draft.items[index]));
    if (next) {
      draft.items.splice(index, 1, next);
      patched = true;
    }
  }));
  if (!patched) {
    dispatch(ordersApi.util.invalidateTags([{ type: 'orders', id: orderId }]));
  }
}

export const {
  useCreateOrderMutation,
  useGetEventOrdersQuery,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  useGetEventOrdersExportQuery,
  useUpdateOrderItemMutation,
  useDeleteOrderItemMutation,
  useUpdateOrderStatusMutation,
  useDecodeQrCodeQuery,
  useUpdateUsedOrderItemMutation,
  useSendOrderEmailMutation,
  useChangeOrderEmailMutation,
  useRegenerateOrderQrsMutation,
  useGetOrderByCustomIdQuery,
  useLazyGetPublicOrderByDisplayIdQuery,
  useRequestPublicRefundMutation,
  useUpdateOrderPublicEmailMutation,
  useGetSellerOrdersQuery,
  useGetSellerSummaryQuery,
  useGetOneSellerSummaryQuery,
  useGetOrderTotalsQuery,
  useGetEventOrdersReportQuery,
  useLazyGetEventOrdersReportQuery,
  useGetQRImageQuery,
  useLazyGetQRImageQuery,
  useGetOrderByIdQuery,
  useLazyGetOrderByIdQuery,
  useValidateTicketExclusiveMutation,
  useGetSellerSummaryXlsxMutation,
  useGetOrderReferralDataQuery,
  useGetEventSalesByUserQuery,
  useGetQuickSellerSummaryQuery,
  useGetEventOrderItemsQuery,
  useCreateBatchManualOrdersMutation,
  useLinkPrintedTicketMutation,
  useUnlinkPrintedTicketMutation,
} = ordersApi;
export { ordersApi };
